import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { isEmbedded, getReturnUrl } from '../lib/embedded';
import { ArrowLeft, Check, Loader2, ShieldCheck, Globe, Tag, X, AlertCircle, CheckCircle, CreditCard, Smartphone } from 'lucide-react';
import { RevolutPaymentForm } from '../components/RevolutPaymentForm';
import { CheckoutPromoSection } from '../components/checkout/CheckoutPromoSection';
import { CheckoutCreditsSection } from '../components/checkout/CheckoutCreditsSection';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import GoogleCustomerReviewsOptin from '../components/GoogleCustomerReviewsOptin';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, formatPrice, convertPrice, convertToAud, formatCredits, getDiscountForCode, login, register, refreshSims, currentCurrency, markupLoaded } = useApp();
  const embedded = isEmbedded();

  // State from navigation
  const [country, setCountry] = useState(location.state?.country || null);
  const [plan, setPlan] = useState(location.state?.plan || null);
  const [carriers] = useState(location.state?.carriers || []);

  // Revolut payment state
  const [revolutToken, setRevolutToken] = useState(null);
  const [revolutOrderId, setRevolutOrderId] = useState(null);
  const revolutAmountRef = useRef(null);
  
  // Form state
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState(false);
  const [isReferral, setIsReferral] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);
  
  // Credit state
  const [userCredits, setUserCredits] = useState(0);
  const [applyCredits, setApplyCredits] = useState(false);
  const [creditsToApply, setCreditsToApply] = useState(0);
  
  // Auth state (for guests)
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nationality, setNationality] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  
  // Payment state
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [generatedEsim, setGeneratedEsim] = useState(null);
  const [isGeneratingEsim, setIsGeneratingEsim] = useState(false);
  // True once provisioning has been running a while — swaps the overlay copy
  // from "a few seconds" to a reassuring "taking a little longer" message.
  const [provisioningDelayed, setProvisioningDelayed] = useState(false);
  // Terminal-but-graceful state: the charge succeeded but the live provisioning
  // gave up. Instead of a scary red error we show a "payment received, we're
  // finalising it in the background" screen. Ops is alerted server-side.
  const [provisioningPending, setProvisioningPending] = useState(false);
  const [cardSaveStatus, setCardSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [pendingReference, setPendingReference] = useState('');
  // Phase C — save card at checkout + pay with an existing saved card
  const [saveCard, setSaveCard] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [savedCardBusy, setSavedCardBusy] = useState(false);
  const [savedCardError, setSavedCardError] = useState('');

  // Check for returning from payment completion
  const returnPaymentIntent = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');

  // Load referral code from session storage (from affiliate link popup)
  useEffect(() => {
    const storedReferralCode = sessionStorage.getItem('referral_code');
    if (storedReferralCode && !promoCode && !promoSuccess) {
      setPromoCode(storedReferralCode);
      // Auto-apply the referral code
      handleApplyPromoCode(storedReferralCode);
      sessionStorage.removeItem('referral_code');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user credits
  useEffect(() => {
    const loadUserCredits = async () => {
      if (user?.email) {
        try {
          const response = await axios.get(`${API}/user/credits/${user.email}`);
          if (response.data.success) {
            setUserCredits(response.data.creditBalance || 0);
          }
        } catch (e) {
          console.error('Error loading user credits:', e);
        }
      }
    };
    loadUserCredits();
  }, [user]);

  // Track whether we've completed at least one server-authoritative price
  // refresh. `initializePayment` must NOT call /api/revolut/create-order until
  // this is true — otherwise it uses potentially-stale plan.price computed on
  // the previous page with default markup (2026-07-01 incident).
  const [pricingQuoteLoaded, setPricingQuoteLoaded] = useState(false);

  // On Checkout mount, refresh plan.price from the server-authoritative
  // pricing quote. Prevents stale client-side markup being used (2026-07-01
  // incident): plan.price is computed on the previous page (Home/SimDetails)
  // with whatever markupSettings existed at that moment — if the /markup-
  // settings fetch hadn't finished yet, the default markup was baked in.
  useEffect(() => {
    let cancelled = false;
    const refreshAuthoritativePrice = async () => {
      if (!plan || !country) return;
      const pkgId = plan.packageId || plan.id;
      if (!pkgId) return;
      try {
        const resp = await axios.get(
          `${API}/pricing/quote/${encodeURIComponent(country.id)}/${encodeURIComponent(pkgId)}`
        );
        if (cancelled) return;
        if (resp.data?.success && typeof resp.data.price_aud === 'number') {
          const authoritative = resp.data.price_aud;
          if (Math.abs((plan.price || 0) - authoritative) > 0.02) {
            console.info(
              `[Checkout] refreshing plan price from server-authoritative ` +
              `(was ${plan.price} AUD, now ${authoritative} AUD)`
            );
            setPlan((p) => (p ? { ...p, price: authoritative } : p));
          }
        }
      } catch (e) {
        // Silent — fall back to whatever the plan already has. The server
        // will still refuse a wrong amount at create-order time.
        console.warn('[Checkout] could not fetch pricing quote:', e?.message);
      } finally {
        if (!cancelled) setPricingQuoteLoaded(true);
      }
    };
    refreshAuthoritativePrice();
    return () => { cancelled = true; };
    // Only run once on plan/country change; deliberately don't depend on plan.price
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.packageId, plan?.id, country?.id]);

  useEffect(() => {
    if (returnPaymentIntent && redirectStatus === 'succeeded') {
      setPaymentSuccess(true);
      // Generate eSIM on successful payment return
      generateEsimFromIntent(returnPaymentIntent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnPaymentIntent, redirectStatus]);

  const generateEsimFromIntent = async (intentId) => {
    if (!plan || !country || !user) return;
    
    setIsGeneratingEsim(true);
    try {
      // Record referral if this was a referral purchase
      if (isReferral && referrerInfo?.email) {
        try {
          await axios.post(`${API}/referral/record`, {
            referral_code: promoCode.toUpperCase(),
            referred_email: user.email,
            order_total: total
          });
        } catch (e) {
          console.error('Error recording referral:', e);
        }
      }
      
      // Apply credits if used
      if (applyCredits && creditsAppliedAud > 0) {
        try {
          await axios.post(`${API}/user/credits/apply`, {
            user_email: user.email,
            amount: creditsAppliedAud,
            order_id: intentId
          });
        } catch (e) {
          console.error('Error applying credits:', e);
        }
      }
      
      const response = await axios.post(`${API}/esim/generate`, {
        session_id: intentId,
        package_id: plan.packageId || plan.id,
        user_email: user.email,
        country_id: country.id,
        country_name: country.name,
        plan_name: plan.name,
        data_amount: plan.dataAmount,
        duration_days: plan.durationDays,
        price: total,
        is_unlimited: plan.isUnlimited || false,
        with_call: plan.withCall || false,
        call_amount: plan.callAmount || 0,
        call_unit: plan.callUnit || "",
        with_sms: plan.withSMS || false,
        sms_amount: plan.smsAmount || 0,
        sms_unit: plan.smsUnit || "",
        referral_code: isReferral ? promoCode.toUpperCase() : null,
        promo_code: promoSuccess && promoCode ? promoCode.toUpperCase() : null
      });
      
      if (response.data.success) {
        setGeneratedEsim(response.data.esim);
        // Fire Google Ads conversion event for successful checkout
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {'send_to': 'AW-17261326206/dTF1CJ30tOEaEP7e66ZA'});
        }
        
        // Complete the referral after eSIM is generated (referrer gets credit)
        if (isReferral && referrerInfo?.email) {
          try {
            await axios.post(`${API}/referral/complete`, {
              referred_email: user.email,
              order_id: response.data.esim?.order_id || intentId
            });
          } catch (e) {
            console.error('Error completing referral:', e);
          }
        }
      } else {
        // Graceful: charge went through but provisioning failed — show the
        // reassuring "finalising in background" screen and log for ops.
        setPendingReference(intentId || '');
        setProvisioningPending(true);
        await reportProvisioningFailure(intentId, response.data.detail || response.data.error, response.status);
      }
    } catch (error) {
      console.error('Error generating eSIM:', error);
      setPendingReference(intentId || '');
      setProvisioningPending(true);
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      await reportProvisioningFailure(intentId, detail, error.response?.status || null);
    } finally {
      setIsGeneratingEsim(false);
    }
  };

  // Calculate totals
  const subtotal = plan?.price || 0;
  const discountAmount = subtotal * discount;
  const subtotalAfterDiscount = subtotal - discountAmount;
  
  // IMPORTANT: Calculate the UNDISCOUNTED subtotal in AUD for PaymentIntent creation
  // The server uses this as the base price and applies discounts server-side
  const undiscountedSubtotalInAud = convertPrice(subtotal);
  
  // Calculate credits to apply (in AUD) - apply to discounted amount
  const subtotalInAud = convertPrice(subtotalAfterDiscount);
  const maxCreditsApplicable = applyCredits ? Math.min(userCredits, subtotalInAud) : 0;
  const creditsAppliedAud = maxCreditsApplicable;
  
  // Convert credits back to USD for total calculation
  const creditsInUsd = creditsAppliedAud > 0 ? (creditsAppliedAud / (subtotalInAud / subtotalAfterDiscount || 1)) : 0;
  
  const total = Math.max(0, subtotalAfterDiscount - creditsInUsd);
  const totalInAud = Math.max(0, convertPrice(total));
  const isFullyCoveredByCredits = applyCredits && creditsAppliedAud >= subtotalInAud - 0.01;

  // Track previous currency to detect changes
  const [prevCurrency, setPrevCurrency] = useState(currentCurrency?.code);

  // When currency changes, reset payment intent to recreate with correct amount
  useEffect(() => {
    if (currentCurrency?.code && prevCurrency && currentCurrency.code !== prevCurrency) {
      // Currency has changed - reset payment intent so it gets recreated
      setRevolutToken(null);
      setRevolutOrderId(null);
      revolutAmountRef.current = null;
      setPaymentError('');
      setPrevCurrency(currentCurrency.code);
    } else if (currentCurrency?.code && !prevCurrency) {
      setPrevCurrency(currentCurrency.code);
    }
  }, [currentCurrency?.code, prevCurrency]);

  // Initialize Revolut payment when user and plan are ready
  useEffect(() => {
    const initializePayment = async () => {
      if (!user || !plan || !country || isFullyCoveredByCredits) return;
      if (totalInAud < 0.50) return;
      // Do NOT create a Revolut order until the authoritative markup grid
      // has finished loading — otherwise we'd send a wrong `amount` (client
      // used default markup instead of admin-configured). Incident 2026-07-01.
      if (!markupLoaded) return;
      // Also wait for the authoritative pricing-quote refresh so that
      // plan.price is guaranteed to match the server. This prevents the
      // "The price for this package has changed" toast from ever firing on
      // legitimate first loads.
      if (!pricingQuoteLoaded) return;

      // BUG FIX 2026-07-21 (customer report: charged AUD number in USD).
      // `total` is in AUD (plan.price is authoritative AUD). To charge the
      // customer in their selected currency, we must convert AUD → target
      // currency here. Prior code sent the AUD numeric value with a USD
      // (or other) currency label, causing e.g. A$41 to be charged as $41 USD
      // instead of the correct ~$26 USD.
      const displayAmount = parseFloat(convertPrice(total).toFixed(2));
      const currencyCode = currentCurrency?.code || 'AUD';
      // Skip if we already have a token for this exact amount+currency
      if (revolutToken && revolutAmountRef.current === `${displayAmount}-${currencyCode}`) return;

      try {
        // Server-side price authority: we send both the discounted `amount`
        // and the undiscounted subtotal in AUD so the backend can verify our
        // markup calculation matches its authoritative value. Prevents the
        // race-condition incident (2026-07-01) where users who reached
        // checkout before /api/markup-settings loaded paid the default markup.
        const response = await axios.post(`${API}/revolut/create-order`, {
          amount: displayAmount,
          currency: currencyCode,
          description: `${country.name} eSIM - ${plan.name}`,
          user_email: user.email,
          plan_id: plan.packageId || plan.id,
          country_id: country.id,
          undiscounted_amount_aud: parseFloat((plan?.price || 0).toFixed(2)),
          promo_code: promoSuccess && promoCode ? promoCode.toUpperCase() : null,
          credits_applied_aud: creditsAppliedAud || 0,
        });

        if (response.data.success) {
          setRevolutToken(response.data.token);
          setRevolutOrderId(response.data.order_id);
          revolutAmountRef.current = `${displayAmount}-${currencyCode}`;
          // Clear any stale "price has changed" error from an earlier attempt
          // now that the current one succeeded.
          setPaymentError('');
        } else {
          setPaymentError(response.data.message || 'Failed to initialize payment');
        }
      } catch (error) {
        console.error('Error initializing Revolut payment:', error);
        setPaymentError(error.response?.data?.detail || error.message || 'Failed to initialize payment');
      }
    };

    initializePayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, plan, country, total, totalInAud, isFullyCoveredByCredits, currentCurrency?.code, markupLoaded, pricingQuoteLoaded]);

  const handleApplyPromoCode = async (code) => {
    const codeToApply = code || promoCode.trim();
    if (!codeToApply) return;
    
    setPromoError('');
    setPromoSuccess(false);
    setIsReferral(false);
    setReferrerInfo(null);
    
    // Determine plan type based on country
    const planType = country?.isRegion ? 'regional' : 'local';
    const result = await getDiscountForCode(codeToApply, user?.email, planType, country?.id);
    
    if (result.success && result.discount > 0) {
      setDiscount(result.discount);
      setPromoSuccess(true);
      setIsReferral(result.isReferral || false);
      
      if (result.isReferral) {
        setReferrerInfo({
          name: result.referrerName,
          email: result.referrerEmail
        });
      }
      
      setRevolutToken(null);
      setRevolutOrderId(null);
      revolutAmountRef.current = null;
    } else {
      setPromoError(result.message || 'Invalid or expired code');
      setDiscount(0);
    }
  };

  const handleApplyPromo = async () => {
    await handleApplyPromoCode();
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setDiscount(0);
    setPromoSuccess(false);
    setPromoError('');
    setIsReferral(false);
    setReferrerInfo(null);
    setRevolutToken(null);
    setRevolutOrderId(null);
    revolutAmountRef.current = null;
  };
  
  const handleToggleCredits = () => {
    setApplyCredits(!applyCredits);
    setRevolutToken(null);
    setRevolutOrderId(null);
    revolutAmountRef.current = null;
    setPaymentError('');
  };

  // Handle order paid entirely with credits (no payment processor needed)
  const handleCreditsOnlyOrder = async () => {
    if (!user || !plan || !country) return;
    setIsGeneratingEsim(true);
    setPaymentError('');
    
    try {
      // Record referral if applicable
      if (isReferral && referrerInfo?.email) {
        try {
          await axios.post(`${API}/referral/record`, {
            referral_code: promoCode.toUpperCase(),
            referred_email: user.email,
            order_total: creditsAppliedAud
          });
        } catch (e) {
          console.error('Error recording referral:', e);
        }
      }

      // Apply credits
      try {
        await axios.post(`${API}/user/credits/apply`, {
          user_email: user.email,
          amount: creditsAppliedAud,
          order_id: `credits-${Date.now()}`
        });
      } catch (e) {
        console.error('Error applying credits:', e);
      }

      // Generate eSIM directly (no payment needed)
      const response = await axios.post(`${API}/esim/generate`, {
        session_id: `credits-${Date.now()}`,
        package_id: plan.packageId || plan.id,
        user_email: user.email,
        country_id: country.id,
        country_name: country.name,
        plan_name: plan.name,
        data_amount: plan.dataAmount,
        duration_days: plan.durationDays,
        price: creditsAppliedAud,
        is_unlimited: plan.isUnlimited || false,
        with_call: plan.withCall || false,
        call_amount: plan.callAmount || 0,
        call_unit: plan.callUnit || "",
        with_sms: plan.withSMS || false,
        sms_amount: plan.smsAmount || 0,
        sms_unit: plan.smsUnit || "",
        promo_code: promoSuccess && promoCode ? promoCode.toUpperCase() : null,
        referral_code: isReferral ? promoCode.toUpperCase() : null,
      });

      if (response.data.success) {
        setGeneratedEsim(response.data.esim);
        setPaymentSuccess(true);
        
        // Refresh the SIMs data so it appears immediately in the dashboard
        if (refreshSims) {
          refreshSims();
        }
        
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {'send_to': 'AW-17261326206/dTF1CJ30tOEaEP7e66ZA'});
        }
        if (isReferral && referrerInfo?.email) {
          try {
            await axios.post(`${API}/referral/complete`, {
              referred_email: user.email,
              order_id: response.data.esim?.order_id || `credits-${Date.now()}`
            });
          } catch (e) {
            console.error('Error completing referral:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error processing credits-only order:', error);
      const errorMessage = error.response?.data?.detail || 'Something went wrong with your payment, please try again.';
      setPaymentError(errorMessage);
    } finally {
      setIsGeneratingEsim(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      let success = false;
      if (authMode === 'login') {
        success = await login(email, password);
      } else {
        success = await register({ email, password, firstName, lastName, nationality });
      }

      if (!success) {
        setAuthError(authMode === 'login' ? 'Invalid credentials' : 'Registration failed. Email may already exist.');
      }
    } catch (error) {
      setAuthError('An error occurred. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    // Store checkout state in sessionStorage before redirecting
    sessionStorage.setItem('checkoutState', JSON.stringify({ country, plan }));
    try {
      const response = await axios.get(`${API}/auth/google/url`);
      const { url } = response.data;
      sessionStorage.setItem('authRedirect', '/checkout');
      window.location.href = url;
    } catch (err) {
      setAuthError('Failed to initiate Google login');
      setIsGoogleLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setIsAppleLoading(true);
    // Store checkout state in sessionStorage before redirecting
    sessionStorage.setItem('checkoutState', JSON.stringify({ country, plan }));
    try {
      // Get the Apple OAuth URL from the backend, passing our origin explicitly
      const currentOrigin = window.location.origin;
      const response = await axios.get(`${API}/auth/apple/url?frontend_origin=${encodeURIComponent(currentOrigin)}`);
      const { url } = response.data;
      sessionStorage.setItem('authRedirect', '/checkout');
      window.location.href = url;
    } catch (err) {
      setAuthError('Failed to initiate Apple login');
      setIsAppleLoading(false);
    }
  };

  const handlePaymentError = (msg) => {
    setPaymentError(msg);
  };

  // Phase C — one-tap checkout charging the user's saved card (customer-initiated).
  const payWithSavedCard = async () => {
    if (savedCardBusy || !user?.email) return;
    setSavedCardBusy(true);
    setSavedCardError('');
    try {
      const resp = await axios.post(`${API}/esim/checkout-saved-card`, {
        user_email: user.email,
        plan_id: plan?.packageId || plan?.id || '',
        country_id: country?.id || '',
        payment_method_id: selectedCardId || savedCard?.revolut_payment_method_id || '',
        currency: currentCurrency?.code || 'AUD',
        browser_env: {
          tz_offset: -new Date().getTimezoneOffset(),
          color_depth: window.screen?.colorDepth || 24,
          screen_width: window.screen?.width || 1920,
          screen_height: window.screen?.height || 1080,
          browser_url: window.location.href,
        },
      });
      if (resp.data?.success && resp.data.session_id) {
        await handlePaymentSuccess(resp.data.session_id);
      } else if (resp.data?.mode === 'processing') {
        // Payment is still settling at Revolut. Do NOT ask for the card again
        // (would double-charge). Show the graceful "payment received, finalising"
        // screen — provisioning + the auto-refund sweep reconcile it.
        setPendingReference(resp.data.revolut_order_id || '');
        setProvisioningPending(true);
      } else {
        setSavedCardError("We couldn't charge your saved card automatically — please enter your card below to complete payment.");
      }
    } catch (e) {
      setSavedCardError(e.response?.data?.detail || 'Could not use your saved card. Please enter your card below.');
    } finally {
      setSavedCardBusy(false);
    }
  };

  const handlePaymentSuccess = async (intentId) => {
    // the eSIM was actually provisioned. Incident 2026-06-18: customer was
    // charged but Roamify failed silently and the user was auto-redirected to
    // an empty dashboard with no order, no email.
    setIsGeneratingEsim(true);
    setPaymentError('');

    // For Revolut payments, save the payment method for future use only when
    // the user ticked "Save this card". The backend resolves the saved card
    // from Revolut's customer payment-methods list (which populates a little
    // after payment), finishing in the background if needed — so we treat any
    // non-rejection as "will be saved" and never show a scary error on the
    // success screen.
    if (saveCard && typeof intentId === 'string' && intentId.startsWith('revolut-') && user?.email) {
      const revolutOrderId = intentId.replace('revolut-', '');
      if (revolutOrderId) {
        setCardSaveStatus('saving');
        axios.post(`${API}/revolut/save-payment-method`, {
          revolut_order_id: revolutOrderId,
          user_email: user.email,
        })
          .then(() => setCardSaveStatus('saved'))
          .catch((err) => {
            // Still resolving server-side; the card appears in the account
            // shortly. Don't alarm the user mid-success.
            console.warn('Save card still processing:', err?.message);
            setCardSaveStatus('saved');
          });
      }
    }

    const esimPayload = {
      session_id: intentId,
      package_id: plan.packageId || plan.id,
      user_email: user.email,
      country_id: country.id,
      country_name: country.name,
      plan_name: plan.name,
      data_amount: plan.dataAmount,
      duration_days: plan.durationDays,
      price: total,
      is_unlimited: plan.isUnlimited || false,
      with_call: plan.withCall || false,
      call_amount: plan.callAmount || 0,
      call_unit: plan.callUnit || "",
      with_sms: plan.withSMS || false,
      sms_amount: plan.smsAmount || 0,
      sms_unit: plan.smsUnit || "",
      promo_code: promoSuccess && promoCode ? promoCode.toUpperCase() : null,
      referral_code: isReferral ? promoCode.toUpperCase() : null,
    };

    // Retry up to 5 times. Most failures here are transient: Revolut state not
    // propagated yet, Roamify briefly unreachable, or apply not ready. Only
    // give up on:
    //   - 401/403/404: terminal client error, retry won't help
    //   - 5th attempt: hard fail and report
    const MAX_ATTEMPTS = 5;
    const BACKOFFS_MS = [0, 1500, 2500, 4000, 6000];
    let lastError = null;
    let lastStatus = null;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        if (BACKOFFS_MS[attempt - 1] > 0) {
          await new Promise(r => setTimeout(r, BACKOFFS_MS[attempt - 1]));
        }
        try {
          const response = await axios.post(`${API}/esim/generate`, esimPayload, { timeout: 60000 });

          if (response.data && response.data.success) {
            setGeneratedEsim(response.data.esim);
            // NOW it's safe to show the success view + auto-redirect
            setPaymentSuccess(true);

            // Refresh the SIMs data so it appears immediately in the dashboard
            if (refreshSims) refreshSims();

            // Fire Google Ads conversion event for successful checkout
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'conversion', { send_to: 'AW-17261326206/dTF1CJ30tOEaEP7e66ZA' });
            }
            return; // ✅ success — exit retry loop
          }
          // Backend returned 2xx but success=false. Treat as retryable.
          lastError = response.data?.detail || response.data?.error || 'eSIM provisioning failed';
          lastStatus = response.status;
          console.warn(`eSIM gen attempt ${attempt}/${MAX_ATTEMPTS} non-success:`, lastError);
        } catch (error) {
          lastError = error.response?.data?.detail || error.message || 'Unknown error';
          lastStatus = error.response?.status || null;
          console.warn(`eSIM gen attempt ${attempt}/${MAX_ATTEMPTS} failed:`, lastStatus, lastError);
          // Terminal errors — don't keep retrying
          if (lastStatus === 401 || lastStatus === 403 || lastStatus === 404) {
            break;
          }
        }
      }

      // All retries exhausted (or terminal error) — surface failure
      console.error('eSIM provisioning failed after retries:', lastStatus, lastError);
      // Graceful: payment already succeeded, so DON'T show a scary error.
      // Show the "we're finalising in the background" screen and log for ops.
      setPendingReference(typeof intentId === 'string' ? intentId : '');
      setProvisioningPending(true);
      await reportProvisioningFailure(intentId, lastError, lastStatus);
    } finally {
      setIsGeneratingEsim(false);
    }
  };

  // Best-effort: tell the backend a paid order failed to provision so ops can refund/retry.
  const reportProvisioningFailure = async (intentId, detail, httpStatus) => {
    try {
      await axios.post(`${API}/orders/provisioning-failure`, {
        session_id: intentId,
        user_email: user?.email || '',
        package_id: plan?.packageId || plan?.id || '',
        country_id: country?.id || '',
        country_name: country?.name || '',
        plan_name: plan?.name || '',
        data_amount: plan?.dataAmount || 0,
        duration_days: plan?.durationDays || 0,
        amount_aud: totalInAud || 0,
        amount_charged: total || 0,
        currency: currentCurrency?.code || 'AUD',
        error_detail: detail,
        http_status: httpStatus,
      });
    } catch (e) {
      console.warn('Could not record provisioning failure:', e?.message);
    }
  };

  // While provisioning runs, flip to a reassuring "taking a little longer"
  // message after 20s so the customer never stares at a bare spinner.
  useEffect(() => {
    if (!isGeneratingEsim) {
      setProvisioningDelayed(false);
      return;
    }
    const t = setTimeout(() => setProvisioningDelayed(true), 20000);
    return () => clearTimeout(t);
  }, [isGeneratingEsim]);

  // Fetch the user's saved cards (if any) so we can offer one-tap checkout.
  useEffect(() => {
    if (!user?.email) { setSavedCard(null); setSavedCards([]); setSelectedCardId(''); return; }
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/payment-methods/${encodeURIComponent(user.email)}`);
        if (!active) return;
        const list = r.data?.cards || [];
        setSavedCards(list);
        const def = list.find((c) => c.is_default) || list[0] || null;
        setSavedCard(def);
        setSelectedCardId(def?.revolut_payment_method_id || '');
      } catch (e) { /* no saved card — ignore */ }
    })();
    return () => { active = false; };
  }, [user?.email]);

  // Auto-redirect after payment success. Same routine as the website (success
  // animation then off to My eSIMs); in the native app we bounce the in-app
  // browser back to the app's return URL so it auto-closes on My eSIMs.
  useEffect(() => {
    if (paymentSuccess) {
      const timer = setTimeout(() => {
        const ret = embedded ? getReturnUrl() : null;
        if (ret) {
          window.location.href = ret;
        } else {
          navigate('/dashboard');
        }
      }, 12000); // 12 seconds
      return () => clearTimeout(timer);
    }
  }, [paymentSuccess, navigate, embedded]);

  // Provisioning-pending View — charge succeeded but the live provisioning
  // gave up. Reassure the customer (no scary error): their eSIM is being
  // finalised in the background and will arrive by email; ops is alerted.
  if (provisioningPending) {
    const ref = pendingReference || '';
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex flex-col" data-testid="provisioning-pending-view">
        {!embedded && <Navbar />}
        <div className="flex-1 flex items-center justify-center pt-20 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-9 h-9 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-3">
              Payment received — finalising your eSIM
            </h1>
            <p className="text-gray-600 dark:text-neutral-400 leading-relaxed mb-2">
              Your payment went through. We&apos;re finalising your eSIM now — if it&apos;s
              ready it&apos;ll be emailed to you and appear in <strong>My eSIMs</strong> within
              a few minutes.
            </p>
            <p className="text-sm text-gray-500 dark:text-neutral-500 leading-relaxed mb-6">
              If we can&apos;t finalise it, we&apos;ll <strong>automatically refund you</strong> and
              email a link to try again — no action needed on your part.
              {ref ? <> Your reference: <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-neutral-300">{ref}</code></> : ''}
            </p>
            <button
              data-testid="provisioning-pending-dashboard-btn"
              onClick={() => {
                const ret = embedded ? getReturnUrl() : null;
                if (ret) { window.location.href = ret; } else { navigate('/dashboard'); }
              }}
              className="px-8 py-4 bg-black text-white dark:bg-white dark:text-black rounded-xl font-bold"
            >
              Go to My eSIMs
            </button>
          </div>
        </div>
        {!embedded && <Footer />}
      </div>
    );
  }

  // Payment Success View - Loading animation with redirect
  if (paymentSuccess) {
    const optinOrderId = generatedEsim?.order_id || generatedEsim?.id || `order-${Date.now()}`;
    const optinEmail = generatedEsim?.user_email || user?.email || email;
    // eSIMs are digital; delivered by email the moment /esim/generate returns.
    // Google requires a country code — use the customer's plan country, and
    // fall back to Australia (our business locale) for regionals/globals.
    const rawCountry = (country?.id || 'AU').toString().toUpperCase();
    const optinCountry = rawCountry.length === 2 ? rawCountry : 'AU';
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex flex-col">
        {!embedded && <Navbar />}
        <div className="flex-1 flex items-center justify-center pt-20 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <Loader2 className="w-16 h-16 animate-spin text-emerald-600 dark:text-emerald-400 mx-auto mb-6" />
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-4">Generating your eSIM</h1>
            <p className="text-gray-600 dark:text-neutral-400">Please wait while we set up your eSIM...</p>
            {cardSaveStatus && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-4 flex items-center justify-center gap-2" data-testid="card-save-status">
                <CheckCircle className="w-4 h-4" />
                Your card has been saved to your account.
              </p>
            )}
          </div>
        </div>

        {/* Google Customer Reviews opt-in — Merchant ID 5830611479. Shows
            a floating badge invitation for the customer to receive a
            review survey after this purchase. */}
        {optinEmail && (
          <GoogleCustomerReviewsOptin
            orderId={optinOrderId}
            email={optinEmail}
            country={optinCountry}
            deliveryDate={new Date().toISOString().slice(0, 10)}
          />
        )}

        <PWAInstallPrompt trigger={true} />
        {!embedded && <Footer />}
      </div>
    );
  }

  // No plan selected view
  if (!plan || !country) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex flex-col">
        {!embedded && <Navbar />}
        <div className="flex-1 flex items-center justify-center pt-20 pb-20 px-4 text-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-4">No plan selected</h1>
            <p className="text-gray-600 dark:text-neutral-400 mb-6">Please select a plan to proceed with checkout.</p>
            <button
              onClick={() => navigate('/destinations')}
              className="px-8 py-4 bg-black text-white rounded-xl font-bold"
            >
              Browse Destinations
            </button>
          </div>
        </div>
        {!embedded && <Footer />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex flex-col overflow-x-hidden">
      {!embedded && <Navbar />}

      {/* "Nearly there!" overlay — shown while /esim/generate is running,
          i.e. between Revolut's onSuccess and the paymentSuccess page transition.
          Prevents the page from appearing to "hang" during the ~1-10s
          provisioning window. Hidden immediately if generation fails so the
          user can see the paymentError on the checkout page. */}
      {isGeneratingEsim && !paymentSuccess && !paymentError && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          role="dialog"
          aria-live="polite"
          aria-modal="true"
          data-testid="nearly-there-overlay"
        >
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl px-8 py-10 max-w-sm w-full text-center animate-in fade-in zoom-in duration-200">
            <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
              <Loader2 className="w-9 h-9 animate-spin text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2
              className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-2"
              data-testid="nearly-there-title"
            >
              {provisioningDelayed ? 'Almost done…' : 'Nearly there!'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-neutral-400 leading-relaxed">
              {provisioningDelayed
                ? 'Payment approved. Your eSIM is taking a little longer than usual to finalise — hang tight, we\u2019re still working on it.'
                : 'Payment approved — finalising your eSIM now. This usually takes a few seconds.'}
            </p>
            <p className="text-xs text-gray-400 dark:text-neutral-500 mt-4">
              Please don&apos;t close this page.
            </p>
          </div>
        </div>
      )}

      <div className={`flex-1 pb-20 ${embedded ? 'pt-6' : 'pt-28'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          {!embedded && (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 dark:text-neutral-400 hover:text-gray-900 mb-8"
            >
              <ArrowLeft size={20} /> Back
            </button>
          )}

          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-neutral-100 mb-8">Checkout</h1>

          <div className="grid lg:grid-cols-5 gap-8 min-w-0">
            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6 min-w-0">
              {/* Order Summary Card */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-4">Your Order</h2>
                <div className="flex items-start gap-4">
                  {/* Use Globe icon for regional plans, flag for countries */}
                  {country.isRegion ? (
                    <div className="w-16 h-12 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg shadow flex items-center justify-center flex-shrink-0">
                      <Globe size={24} className="text-white" />
                    </div>
                  ) : (
                    <img
                      src={country.flag}
                      alt={country.name}
                      className="w-16 h-12 rounded-lg object-cover shadow flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 dark:text-neutral-100">{country.name}</h3>
                    <p className="text-gray-600 dark:text-neutral-400">
                      {plan.isUnlimited ? 'Unlimited Data' : `${plan.dataAmount}GB`} • {plan.durationDays} days
                    </p>
                    {carriers.length > 0 && (
                      <p className="text-sm text-gray-500 dark:text-neutral-500 mt-1">{carriers.join(', ')}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {discount > 0 ? (
                      <>
                        <p className="text-sm text-gray-400 dark:text-neutral-500 line-through" data-testid="order-price-original">
                          {formatPrice(plan.price)}
                        </p>
                        <p className="text-xl font-black text-emerald-600 dark:text-emerald-400" data-testid="order-price-discounted">
                          {formatPrice(plan.price * (1 - discount))}
                        </p>
                      </>
                    ) : (
                      <p className="text-xl font-black text-gray-900 dark:text-neutral-100" data-testid="order-price">
                        {formatPrice(plan.price)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Auth Section (for guests) */}
              {!user && (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-4">
                    {authMode === 'login' ? 'Sign In to Continue' : 'Create Account'}
                  </h2>

                  {/* Google Sign In Button */}
                  <button
                    onClick={handleGoogleLogin}
                    disabled={isGoogleLoading || isAppleLoading}
                    className="w-full py-3.5 px-4 bg-white dark:bg-neutral-900 border-2 border-gray-200 dark:border-neutral-800 rounded-xl font-semibold text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:border-gray-300 transition-all flex items-center justify-center gap-3 mb-3 disabled:opacity-50"
                    data-testid="checkout-google-signin-btn"
                  >
                    {isGoogleLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </>
                    )}
                  </button>

                  {/* Apple Sign In Button */}
                  <button
                    onClick={handleAppleLogin}
                    disabled={isGoogleLoading || isAppleLoading}
                    className="w-full py-3.5 px-4 bg-black border-2 border-black dark:border-neutral-300 rounded-xl font-semibold text-white hover:bg-gray-900 transition-all flex items-center justify-center gap-3 mb-4 disabled:opacity-50"
                    data-testid="checkout-apple-signin-btn"
                  >
                    {isAppleLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                        Continue with Apple
                      </>
                    )}
                  </button>

                  {/* Divider */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700"></div>
                    <span className="text-sm text-gray-500 dark:text-neutral-500">or continue with email</span>
                    <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700"></div>
                  </div>

                  <form onSubmit={handleAuth} className="space-y-4">
                    {authMode === 'register' && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">First Name</label>
                            <input
                              type="text"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Last Name</label>
                            <input
                              type="text"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black"
                        data-testid="checkout-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black"
                        data-testid="checkout-password"
                      />
                    </div>

                    {authError && (
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                        <AlertCircle size={16} /> {authError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={authLoading}
                      className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50"
                      data-testid="checkout-auth-btn"
                    >
                      {authLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                      ) : (
                        authMode === 'login' ? 'Sign In' : 'Create Account'
                      )}
                    </button>
                  </form>

                  <div className="mt-4 text-center">
                    <button
                      onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); }}
                      className="text-sm text-gray-600 dark:text-neutral-400 hover:text-black dark:hover:text-white"
                    >
                      {authMode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                    </button>
                  </div>
                </div>
              )}

              {/* Promo Code & Referral */}
              <CheckoutPromoSection
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                promoSuccess={promoSuccess}
                promoError={promoError}
                setPromoError={setPromoError}
                discount={discount}
                isReferral={isReferral}
                referrerInfo={referrerInfo}
                onApply={handleApplyPromo}
                onRemove={handleRemovePromo}
              />

              {/* Account Credits Section */}
              {user && (
                <CheckoutCreditsSection
                  userCredits={userCredits}
                  applyCredits={applyCredits}
                  onToggle={handleToggleCredits}
                  amountToApply={Math.min(userCredits, convertPrice(subtotalAfterDiscount))}
                  formatCredits={formatCredits}
                />
              )}

              {/* Payment Section */}
              {user && (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-4 flex items-center gap-2">
                    <CreditCard size={20} /> Payment
                  </h2>
                  
                  {paymentError && (
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-4 p-3 bg-red-50 dark:bg-red-500/10 rounded-xl">
                      <AlertCircle size={20} />
                      <span>{paymentError}</span>
                    </div>
                  )}

                  {isFullyCoveredByCredits ? (
                    <div className="text-center py-6">
                      <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check size={28} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <p className="text-gray-700 dark:text-neutral-300 font-semibold mb-1">Fully covered by credits</p>
                      <p className="text-sm text-gray-500 dark:text-neutral-500 mb-6">A${creditsAppliedAud.toFixed(2)} will be deducted from your balance</p>
                      <button
                        onClick={handleCreditsOnlyOrder}
                        disabled={isGeneratingEsim}
                        className="w-full py-4 bg-black text-white rounded-xl font-bold text-base hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="pay-with-credits-btn"
                      >
                        {isGeneratingEsim ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                        ) : (
                          <>Complete Order with Credits</>
                        )}
                      </button>
                    </div>
                  ) : revolutToken ? (
                    <div className="space-y-4">
                      <RevolutPaymentForm
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        formatPrice={formatPrice}
                        total={total}
                        totalInAud={totalInAud}
                        revolutToken={revolutToken}
                        revolutOrderId={revolutOrderId}
                        user={user}
                        country={country}
                        plan={plan}
                        currentCurrency={currentCurrency}
                        savePaymentMethod={false}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-neutral-500" />
                      <span className="ml-3 text-gray-600 dark:text-neutral-400">Initializing secure payment...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - Price Summary */}
            <div className="hidden lg:block lg:col-span-2 min-w-0">
              <div className="sticky top-32 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-950">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Price Summary</h3>
                </div>
                
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-neutral-400">Subtotal</span>
                    <span className="font-semibold text-gray-900 dark:text-neutral-100">{formatPrice(subtotal)}</span>
                  </div>
                  
                  {discount > 0 && (
                    <div className="flex justify-between items-center text-green-600 dark:text-emerald-400">
                      <span>{isReferral ? 'Referral Discount' : 'Discount'} ({(discount * 100).toFixed(0)}%)</span>
                      <span className="font-semibold">-{formatPrice(discountAmount)}</span>
                    </div>
                  )}
                  
                  {applyCredits && creditsAppliedAud > 0 && (
                    <div className="flex justify-between items-center text-emerald-600 dark:text-emerald-400">
                      <span>Credits Applied</span>
                      <span className="font-semibold">-{formatCredits(creditsAppliedAud)}</span>
                    </div>
                  )}
                  
                  <div className="h-px bg-gray-100 dark:bg-neutral-800" />
                  
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900 dark:text-neutral-100">Total</span>
                    <div className="text-right">
                      <span className="text-2xl font-black text-gray-900 dark:text-neutral-100">{isFullyCoveredByCredits ? formatCredits(0) : formatPrice(total)}</span>
                      {applyCredits && creditsAppliedAud > 0 && (
                        <p className="text-xs text-gray-500 dark:text-neutral-500">+ {formatCredits(creditsAppliedAud)} credits</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-6">
                  <div className="space-y-3 text-sm text-gray-600 dark:text-neutral-400">
                    <div className="flex items-center gap-2">
                      <Globe size={16} className="text-blue-600 dark:text-blue-400" />
                      <span>Coverage in {country.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Smartphone size={16} className="text-green-600 dark:text-emerald-400" />
                      <span>Easy one-tap install on Android and iOS</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!embedded && <Footer />}
    </div>
  );
};

export default Checkout;
